import { ReactNode } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { OperatorApp } from '@/features/operator-selection/types/operator-types';
import SelectOperatorPage from '@/features/operator-selection/SelectOperatorPage';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * ProtectedRoute - Route Guard Component
 * Flow: Check auth → Check user type → Auto-select for operators → Show selector for distributors
 *
 * Matches Flutter pattern from EnergyFlutter/select_operator_bloc.dart:
 * - Distributor users (OperatorId === DistributorId): Show operator selector
 * - Operator users (OperatorId !== DistributorId): Skip selector, auto-select from JWT
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, distributorId, operatorId, isDistributor } = useAuthStore();
  const { seenOperatorSelector, selectedOperator, checkDistributorChange, selectOperator } = useOperatorStore();
  const { operatorId: routeOperatorId, machineId: routeMachineId } = useParams<{ operatorId?: string; machineId?: string }>();
  const [searchParams] = useSearchParams();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // For operator users (non-distributors), auto-select from JWT and skip selector
  // This matches Flutter's pattern: if (!isDistributor) status = notNeed
  // The /actor/apps API returns empty for operator users, so we create OperatorApp from JWT
  if (!isDistributor && operatorId && !selectedOperator) {
    const operatorFromJwt: OperatorApp = {
      operatorId: parseInt(operatorId),
      name: operatorId, // Use operatorId as name (operator users don't need display name)
      appId: 0,
      slug: '',
      distributorId: distributorId ? parseInt(distributorId) : 0,
      useThreeDs: false,
    };
    selectOperator(operatorFromJwt, distributorId);
  }

  // Detect URL-provided context — used to skip the selector and auto-select the operator.
  // hasOperatorContext is true when:
  //   1. actorId query param is numeric, OR
  //   2. :operatorId path param exists with any value — even "{ActorId}" template placeholder
  const urlActorId = searchParams.get('actorId') || routeOperatorId;
  const parsedActorId = parseInt(urlActorId || '', 10);
  const hasNumericActorId = !isNaN(parsedActorId);
  const hasOperatorContext = hasNumericActorId || (routeOperatorId !== undefined);
  const hasMachineContext = !!(routeMachineId || searchParams.get('machineId'));

  // For distributor users: if the URL has a numeric operator ID, auto-select it
  // so downstream components can read it from the store.
  if (isDistributor && !selectedOperator && hasNumericActorId) {
    const operatorFromUrl: OperatorApp = {
      operatorId: parsedActorId,
      name: String(parsedActorId),
      appId: 0,
      slug: '',
      distributorId: distributorId ? parseInt(distributorId) : 0,
      useThreeDs: false,
    };
    selectOperator(operatorFromUrl, distributorId);
  }

  // Check if distributor has changed since last operator selection
  const hasDistributorChanged = checkDistributorChange(distributorId);

  // Show operator selector page ONLY for distributors if:
  // 1. User is a distributor, AND
  // 2. No operator/machine context is already in the URL (Assign EV / Assign Kiosk routes), AND
  // 3. User has never selected an operator, OR no operator selected, OR distributor changed
  const shouldShowOperatorSelector =
    isDistributor &&
    !hasOperatorContext &&
    !hasMachineContext &&
    (!seenOperatorSelector || !selectedOperator || hasDistributorChanged);

  if (shouldShowOperatorSelector) {
    return <SelectOperatorPage />;
  }

  return <>{children}</>;
};
